import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    addressResponseSchema,
    entityEmailAddressesResponseSchema,
    createEntityBodySchema,
    createLogoUploadUrlBodySchema,
    createLogoUploadUrlResponseSchema,
    reorderAccountsBodySchema,
    entityMcpContextResponseSchema,
    saveTaxLocationsBodySchema,
    searchEntitiesQuerySchema,
    searchEntitiesResponseSchema,
    updateEntityAddressBodySchema,
    updateEntityBodySchema,
    updateEntityBookkeepingStartDateBodySchema,
    updateLogoBodySchema,
} from "@common/schemas/entity.schema";
import { Entity } from "../types/entity";

const c = initContract();

export const entityContract = c.router(
    {
        createEntity: {
            method: "POST",
            path: "/",
            body: createEntityBodySchema,
            responses: {
                201: c.type<Entity>(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        searchEntities: {
            method: "GET",
            path: "/search",
            query: searchEntitiesQuerySchema,
            responses: {
                200: searchEntitiesResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateEntity: {
            method: "PATCH",
            path: "/:entityId",
            pathParams: z.object({
                entityId: z.coerce.number(),
            }),
            body: updateEntityBodySchema,
            responses: {
                200: c.type<Entity>(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        updateBookkeepingStartDate: {
            method: "PATCH",
            path: "/:entityId/bookkeeping-start-date",
            pathParams: z.object({
                entityId: z.coerce.number(),
            }),
            body: updateEntityBookkeepingStartDateBodySchema,
            responses: {
                200: c.type<Entity>(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        deleteEntity: {
            method: "DELETE",
            path: "/:entityId",
            pathParams: z.object({
                entityId: z.coerce.number(),
            }),
            body: c.noBody(),
            responses: {
                200: c.noBody(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
                500: ErrorMessageSchema,
            },
        },
        reorderAccounts: {
            method: "POST",
            path: "/reorder-accounts",
            body: reorderAccountsBodySchema,
            responses: {
                201: c.noBody(),
                404: ErrorMessageSchema,
            },
        },
        saveTaxLocations: {
            method: "POST",
            path: "/tax-location",
            body: saveTaxLocationsBodySchema,
            responses: {
                200: c.type<Entity>(),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getAddress: {
            method: "GET",
            path: "/:entityId/address",
            pathParams: z.object({
                entityId: z.coerce.number(),
            }),
            responses: {
                200: addressResponseSchema,
                404: ErrorMessageSchema,
            },
        },
        getEmailAddresses: {
            method: "GET",
            path: "/:entityId/email-addresses",
            pathParams: z.object({
                entityId: z.coerce.number(),
            }),
            responses: {
                200: entityEmailAddressesResponseSchema,
                404: ErrorMessageSchema,
            },
        },
        getEntityMcpContext: {
            method: "GET",
            path: "/:entityId/mcp-context",
            pathParams: z.object({
                entityId: z.coerce.number(),
            }),
            responses: {
                200: entityMcpContextResponseSchema,
                404: ErrorMessageSchema,
            },
        },
        updateEntityAddress: {
            method: "PUT",
            path: "/:entityId/address",
            pathParams: z.object({
                entityId: z.coerce.number(),
            }),
            body: updateEntityAddressBodySchema,
            responses: {
                200: addressResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        createLogoUploadUrl: {
            method: "POST",
            path: "/:entityId/logo/upload-url",
            pathParams: z.object({
                entityId: z.coerce.number(),
            }),
            body: createLogoUploadUrlBodySchema,
            responses: {
                201: createLogoUploadUrlResponseSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateLogo: {
            method: "PUT",
            path: "/:entityId/logo",
            pathParams: z.object({
                entityId: z.coerce.number(),
            }),
            body: updateLogoBodySchema,
            responses: {
                200: c.type<{ logoUrl: string | null }>(),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    { pathPrefix: "/entity" },
);
